import { isBefore } from "date-fns";
import { Microdollars } from "model/modelTypes";
import { ILineitemBase } from "./lineitem";

export enum LineitemStatus { //displayed on UI
  Paused = "paused",
  AutoPaused = "autoPaused",
  Running = "running",
  Finished = "finished",
  Unknown = "unknown",
}

export enum LineitemRunningStateServer { //values from server
  AutoPaused = "AUTO_PAUSED",
  Stopped = "STOPPED",
  Running = "RUNNING",
}

export interface ILineItemState {
  archived: boolean;
  id: string;
  removable: boolean;
  runState: LineitemRunningStateServer;
  running: boolean;
}

//values from statistics/{lineitem} endpoint on server
export type ILineitemStatusServer = {
  bids: number;
  hourlySpent: Microdollars;
  dailySpent: Microdollars;
  monthlySpent: Microdollars;
  events: {
    install: number;
    click: number;
  };
  impressions: number;
  modelBids: number;
  lineitemState: ILineItemState;
  spent: Microdollars;
};

export const getLineitemStatus = (
  runState: LineitemRunningStateServer,
  finishDate?: Date | null
) => {
  if (finishDate && isBefore(finishDate, new Date())) {
    return LineitemStatus.Finished;
  }
  if (runState === LineitemRunningStateServer.Running)
    return LineitemStatus.Running;
  if (runState === LineitemRunningStateServer.AutoPaused)
    return LineitemStatus.AutoPaused;
  if (runState === LineitemRunningStateServer.Stopped)
    return LineitemStatus.Paused;
  return LineitemStatus.Unknown;
};

export const getLineitemAllowedActions = (runState: LineitemStatus) => {
  const isPauseActionAllowed =
    runState === LineitemStatus.Running ||
    runState === LineitemStatus.AutoPaused;
  const isStartActionAllowed =
    runState === LineitemStatus.Paused ||
    runState === LineitemStatus.AutoPaused;
  return { isStartActionAllowed, isPauseActionAllowed };
};

// The server sends empty and/or lists as `{ type: "WHITELIST", and: false }`
// with no `values` at all, so the presence of the object says nothing — only a
// non-empty `values` counts. The `type` is checked at runtime rather than
// trusted from the TS types: `audienceIfasV2` is typed whitelist-only but real
// payloads do come back as BLACKLIST.
const hasWhitelistValues = (list?: {
  type?: string;
  values?: unknown[] | null;
}): boolean => list?.type === "WHITELIST" && !!list.values?.length;

// Whitelists are stored with empty strings left in by the CSV/table inputs
// until formToServerNormalizeRules strips them, so a list holding nothing but
// blanks is not targeting.
const hasListValues = (list?: string[] | null): boolean =>
  !!list?.filter(Boolean).length;

/**
 * Whether the line item targets an audience by whitelist. Blacklist audiences
 * do not count: they narrow the open internet rather than confine the line item
 * to a known set of users.
 */
export const hasWhitelistAudience = (
  lineitem: Partial<ILineitemBase>
): boolean =>
  hasWhitelistValues(lineitem.audienceIfasV2) ||
  hasWhitelistValues(lineitem.audienceIPsV2) ||
  hasWhitelistValues(lineitem.audienceBundlesV2) ||
  hasWhitelistValues(lineitem.audienceInventoryIdsV2) ||
  hasWhitelistValues(lineitem.audienceDomainsV2) ||
  !!lineitem.atomCohorts?.flat().filter(Boolean).length;

/**
 * Whether the line item is confined to known inventory or a known audience.
 * Without one of these it bids across the open internet, so it must not go
 * live.
 *
 * CTV app bundles are normalised into `bundlesV2` on save
 * (bundlesCTVNormalization), so that one field covers IN_APP and CTV alike.
 * The legacy V1 `bundles` / `domains` fields are deliberately not counted: they
 * are not editable in the form, so honouring them would let a line item start
 * on a whitelist the user cannot see — the same reasoning as
 * hasMobileWebDomainValues().
 */
export const hasTargetingWhitelist = (
  lineitem: Partial<ILineitemBase>
): boolean =>
  hasListValues(lineitem.bundlesV2?.whitelist) ||
  hasListValues(lineitem.placements?.whitelist) ||
  hasListValues(lineitem.domainsV2?.whitelist) ||
  hasWhitelistAudience(lineitem);

// Validation shared by single start, bulk start and campaign start: a line item
// needs a landing, a creative and some form of whitelist targeting before it
// can go live. Returns the missing requirements as phrases (empty array = ready
// to start).
export const getLineitemStartErrors = (
  lineitem: Partial<ILineitemBase>
): string[] => {
  const errs: string[] = [];

  if ((lineitem.landingIds?.length ?? 0) === 0)
    errs.push("add a landing to the line item");
  if ((lineitem.creativeIds?.length ?? 0) === 0)
    errs.push("add a creative to the line item");
  if (!hasTargetingWhitelist(lineitem))
    errs.push(
      "add an app, tag ID or domain whitelist, or a whitelist audience, to the line item"
    );

  return errs;
};

export const DEFAULT_LINEITEM_STATUS_SERVER: Omit<
  ILineitemStatusServer,
  "lineitemState"
> & {
  lineitemState: Omit<ILineItemState, "id">;
} = {
  bids: 0,
  hourlySpent: 0,
  dailySpent: 0,
  monthlySpent: 0,
  events: {
    install: 0,
    click: 0,
  },
  impressions: 0,
  modelBids: 0,
  lineitemState: {
    archived: false,
    removable: true,
    runState: LineitemRunningStateServer.Stopped,
    running: false,
  },
  spent: 0,
};
